import { FacetMatrix } from '@/models/proficiency/facet-matrix';
import { FacetMatrixCount } from '@/models/proficiency/facet-matrix-count';
import { CompetencyPrerequisite } from '@/models/proficiency/competency-prerequisite';

import { Domain } from '@/models/proficiency/domain-topic/domain';
import { Topic } from '@/models/proficiency/domain-topic/topic';
import { Competency } from '@/models/proficiency/domain-topic/competency';
import { sortByProperty } from '@/utils/utils';

export class CompetencySerializer {

  static get instance() {
    return this.INSTANCE;
  }
  private static INSTANCE = new CompetencySerializer();

  public serializeCompetencyModel(competencyModel: any) {
    return competencyModel;
  }
  public serializeDomainCompetencyMatrix(domainCompetencyMatrix: any) {
    const userDomainCompetencyMatrix = domainCompetencyMatrix.userCompetencyMatrix;
    return userDomainCompetencyMatrix;
  }

  public serializeMatrixCoOrdinates(matrixCoOrdinates: any) {
    return matrixCoOrdinates;
  }

  public serializeFacetsCompetencyMatrix(facetsCompetencyMatrix: any) {
    const serializer = this;
    const userSubjectCompetencyMatrix = facetsCompetencyMatrix.userSubjectCompetencyMatrix;
    const serializedFacetsCompetencyMatrix: FacetMatrix[] =
      userSubjectCompetencyMatrix.map( (subjectCompetencyMatrix: any) => {
      const serializedFacetCompetencyMatrix: FacetMatrix = {
        classificationCode: subjectCompetencyMatrix.classificationCode,
        classificationName: subjectCompetencyMatrix.classificationName,
        classificationSeq: subjectCompetencyMatrix.classificationSeq,
        competencyStats: serializer.normalizeFacetCompetencyMatrixCount(subjectCompetencyMatrix.competencyStats),
        subjectCode: subjectCompetencyMatrix.subjectCode,
        subjectName: subjectCompetencyMatrix.subjectName,
        subjectSeq: subjectCompetencyMatrix.subjectSeq,
      };
      return serializedFacetCompetencyMatrix;
    });
    return serializedFacetsCompetencyMatrix;
  }


  public serializePrerequisites(prerequisites: CompetencyPrerequisite[]) {
    const serializedPrerequisites: CompetencyPrerequisite[] =
      prerequisites.map( (prerequisite) => {
        const serializedPrerequisite: CompetencyPrerequisite = {
          code: prerequisite.code,
          id: prerequisite.id,
          title: prerequisite.title,
        };
        return serializedPrerequisite;
      });
    return serializedPrerequisites;
  }

  public serializeDomainTopicMatrix(payload: any) {
    const serializedMatrix: Domain[] = [];
    if (payload && payload.userCompetencyMatrix) {
      const userCompetencyMatrix = payload.userCompetencyMatrix;
      userCompetencyMatrix.map((competencyMatrix: any) => {
        let domainMatrix: any = {
          domainCode: competencyMatrix.domainCode,
          domainSeq: competencyMatrix.domainSeq,
          topics: this.normalizeDomainTopics(competencyMatrix.topics),
        };
        domainMatrix = Object.assign(domainMatrix, this.groupCompetenciesByTopic(domainMatrix.topics));
        serializedMatrix.push(domainMatrix);
      });
    }
    return serializedMatrix;
  }

  public serializeDomainTopicMetadata(payload: any) {
    let serializedMetadata: any[] = [];
    if (payload && payload.domainTopics) {
      serializedMetadata = payload.domainTopics.map((domain: Domain) => {
        const normalizedDomain: Domain = {
          domainCode: domain.domainCode,
          domainName: domain.domainName,
          domainSeq: domain.domainSeq,
          topics: this.normalizeDomainTopicMetadata(domain),
        };
        return normalizedDomain;
      });
    }
    return serializedMetadata.sort((a: any, b: any) => a.domainSeq - b.domainSeq);
  }


  private normalizeFacetCompetencyMatrixCount(facetCompetencyStats: any) {
    const serializedFacetCompetencyMatrixCount: FacetMatrixCount[] =
      facetCompetencyStats.map( (facetCompetencyStat: any) => {
      const facetMatrixCount: FacetMatrixCount = {
        competencyCount: facetCompetencyStat.competencyCount,
        competencyStatus: facetCompetencyStat.competencyStatus,
      };
      return facetMatrixCount;
    });
    return serializedFacetCompetencyMatrixCount;
  }


  private normalizeDomainTopics(topics: any[]) {
    if (topics && topics.length) {
      sortByProperty(topics, 'topicSeq', 'ASC');
      return topics.map((topic: any) => {
        // NOTE need to use type interface instead of any
        let normalizedTopic: any = {
          topicCode: topic.topicCode,
          topicSeq: topic.topicSeq,
          competencies: this.normalizeDomainTopic(topic),
        };
        normalizedTopic = Object.assign(normalizedTopic, this.groupCompetenciesByStatus(topic.competencies));
        return normalizedTopic;
      });
    }
    return [];
  }

  private normalizeDomainTopic(topic: any) {
    const competencies = topic.competencies;
    if (competencies && competencies.length) {
      sortByProperty(competencies, 'competencySeq', 'ASC');
      const masteredCompetencies = 0;
      const inprogressCompetencies = 0;
      const notstartedCompetencies = 0;
      const inferredCompetencies = 0;
      const totalCompetencies = 0;
      return competencies.map((competency: any) => {
        const normaliedCompetency: Competency = {
          competencyCode: competency.competencyCode,
          competencyName: competency.competencyName,
          competencySeq: competency.competencySeq,
          competencyDesc: competency.competencyDesc,
          competencyStudentDesc: competency.competencyStudentDesc,
          competencyDisplayCode: competency.competencyDisplayCode,
          status: competency.status,
          source: competency.source,
        };
        return normaliedCompetency;
      });
    }
    sortByProperty(topic, 'topicSeq', 'ASC');
    return topic;
  }

  private groupCompetenciesByStatus(competencies: any[]) {
    const competenciesCount = {
      masteredCompetencies: 0,
      inprogressCompetencies: 0,
      inferredCompetencies: 0,
      notstartedCompetencies: 0,
    };
    competencies.map((competency: any) => {
      if (competency.status === 0) {
        competenciesCount.notstartedCompetencies++;
      } else if (competency.status === 1) {
        competenciesCount.inprogressCompetencies++;
      } else if ( competency.status === 2 || competency.status === 3) {
        competenciesCount.inferredCompetencies++;
      } else {
        competenciesCount.masteredCompetencies++;
      }
    });
    return competenciesCount;
  }

  private groupCompetenciesByTopic(topics: any[]) {
    let masteredCompetencies = 0;
    let inprogressCompetencies = 0;
    let notstartedCompetencies = 0;
    let inferredCompetencies = 0;
    topics.map((topic) => {
      masteredCompetencies += topic.masteredCompetencies;
      inprogressCompetencies += topic.inprogressCompetencies;
      inferredCompetencies += topic.inferredCompetencies;
      notstartedCompetencies += topic.notstartedCompetencies;
    });
    return {
      masteredCompetencies,
      inprogressCompetencies,
      notstartedCompetencies,
      inferredCompetencies,
      totalCompetencies: masteredCompetencies + inprogressCompetencies + notstartedCompetencies + inferredCompetencies,
    };
  }

  private normalizeDomainTopicMetadata(domain: Domain): Topic[] {
    let serializedTopicsMetadata!: Topic[];
    if (domain.topics && domain.topics.length) {
      serializedTopicsMetadata = domain.topics.map((topic: any) => {
        const normalizedTopicMetadata: Topic = {
          topicCode: topic.topicCode,
          topicDesc: topic.topicDesc,
          topicName: topic.topicName,
          topicSeq: topic.topicSeq,
          domainCode: domain.domainCode,
          domainSeq: domain.domainSeq,
        };
        return normalizedTopicMetadata;
      });
    }
    sortByProperty(serializedTopicsMetadata, 'topicSeq', 'ASC');
    return serializedTopicsMetadata;
  }

}

export const competencySerializer = CompetencySerializer.instance;
